import { Archive, ArchiveRestore, Crosshair, ExternalLink, Film, Music, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { createJob, updateBacklog, type BacklogEntry, type MusicTrack } from "../api";
import { prettifyTrackName } from "../lib/music";
import { buildSceneJobInput } from "../lib/sceneJobInput";
import { SceneDetailModal } from "./SceneDetailModal";
import { ctaSmallStyle } from "./styles";
import { Select } from "./ui/Select";

const DEFAULT_CHANNEL_SLUG = "quotologies";

/**
 * "SceneRow" — one scanned-scene candidate, rendered both in the Backlog's
 * inline "Scanned scenes" list and (historically) the Scene Library modal.
 * Mirrors the prior ScanCandidateCard (thumbnail, hook, confidence,
 * whyItWorks, time range) and owns its own Render flow.
 *
 * Render paths:
 *   - Un-rendered (no `renderedJobId`): a primary **Render** button →
 *     createJob, then PATCH candidate:false + renderedJobId so the row's
 *     state persists durably.
 *   - Already rendered (`renderedJobId` set): the row stays visible with an
 *     **Open render** action (navigates to the existing job via
 *     `onNavigateQueue`) plus a secondary **Render again** that re-runs the
 *     exact same createJob path. Re-rendering never deletes the row and never
 *     crashes on a duplicate slug — clip.ts owns slug collisions server-side.
 */
export function SceneRow({
  entry,
  isMobile,
  channelsEnabled,
  channels,
  confirming,
  musicTracks,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onRendered,
  onEntryUpdated,
  onNavigateQueue,
  onToggleArchive,
}: {
  entry: BacklogEntry;
  isMobile: boolean;
  channelsEnabled?: boolean;
  channels?: { slug: string; displayName: string }[];
  confirming: boolean;
  /** Background-music catalog for the per-scene picker. null/absent → only the
   *  "Auto" option shows (catalog still loading or failed to load). */
  musicTracks?: MusicTrack[] | null;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onRendered: (updated: BacklogEntry, jobId: string) => void;
  /** Persist a non-render edit (e.g. the per-scene music pick) back to the
   *  parent's entries list so it survives a re-render of the Backlog. */
  onEntryUpdated?: (updated: BacklogEntry) => void;
  /** Open an already-rendered scene's job (and the post-render toast's
   *  "Go to Queue"). Absent → the Open-render button is hidden. */
  onNavigateQueue?: (jobId: string) => void;
  /** Archive/restore affordance (archive-not-delete). When present, fresh
   *  rows get an Archive icon button next to the delete trashcan and archived
   *  rows get a Restore button. The parent owns the PATCH — this row only
   *  reports the click, so none of its render/open/delete flows change. */
  onToggleArchive?: () => void;
}): React.JSX.Element {
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  // Scene-details popup — opened by clicking the row's content area. All the
  // pre-render decision info (summary, payoff, ending check, transcript) lives
  // there so the row itself stays scannable.
  const [detailOpen, setDetailOpen] = useState(false);
  // The operator's music pick for THIS scene. Seeded from the candidate (which
  // inherited the New Job form's pick); "" = Auto (pickMusic chooses at render).
  const [musicChoice, setMusicChoice] = useState<string>(entry.musicTrack ?? "");
  const [musicSaving, setMusicSaving] = useState(false);
  // Resync from the prop when it changes out-of-band (a refresh() or another
  // tab/device editing the same scene) — but never mid-write, so an in-flight
  // save can't be clobbered. On success the prop already equals the saved value.
  useEffect(() => {
    if (!musicSaving) setMusicChoice(entry.musicTrack ?? "");
  }, [entry.musicTrack, musicSaving]);

  const thumbnailUrl = entry.videoId
    ? `https://img.youtube.com/vi/${entry.videoId}/hqdefault.jpg`
    : undefined;

  const isRendered = !!entry.renderedJobId;

  // `force` bypasses the already-rendered guard so "Render again" can re-run a
  // scene whose candidate flag is already false. The first-time "Render" button
  // leaves force off so a double-click can't fire two jobs. Resolves true only
  // when the job actually started — the popup's Render button closes on it.
  const handleRender = async (force = false): Promise<boolean> => {
    // Don't fire while a music PATCH is still in flight — racing it would let the
    // music save's stale {candidate:true} body land after the render's PATCH.
    if (rendering || musicSaving) return false;
    if (isRendered && !force) return false;
    setRendering(true);
    setRenderError(null);
    try {
      // Use the candidate's verbatim phrases so clip.ts snaps exactly.
      // autoScan: the host may be a scan GUESS — the pipeline runs roster-aware
      // speaker identification to correct it (or abstain). But when hostSource is
      // "operator" (the operator typed the name), it's authoritative: forward it
      // so the spawn harness locks the name and resolveSpeaker won't override it.
      //
      // The payload is built by buildSceneJobInput — byte-identical to the
      // historical inline object for unadjusted scenes (unit-proven in
      // sceneJobInput.test.ts). Boundary-adjusted scenes flow through the SAME
      // fields (from/to/startPhrase/endPhrase, + noSnap when the start was
      // seconds-nudged), so an adjusted window renders like any operator pin.
      const { id } = await createJob(buildSceneJobInput(entry, musicChoice));
      // Flip the candidate flag + record the (latest) job id — do NOT delete the
      // entry. Omit id + createdAt from the spread: BacklogDraft =
      // Omit<BacklogEntry, "id" | "createdAt">. candidate:false + renderedJobId
      // persist via the server PATCH so the row stays in its rendered state.
      // Persist the (possibly just-changed) music pick alongside it. Send the
      // raw value (NOT `|| undefined`) so an explicit "" reaches the PATCH's
      // `"musicTrack" in body` guard and stores Auto — `undefined` would be
      // dropped by JSON.stringify and silently leave a stale pin.
      const { id: _id, createdAt: _createdAt, ...rest } = entry;
      const updated = await updateBacklog(entry.id, {
        ...rest,
        musicTrack: musicChoice.trim(),
        candidate: false,
        renderedJobId: id,
      });
      onRendered(updated, id);
      return true;
    } catch (err) {
      setRenderError(err instanceof Error ? err.message : "render failed");
      return false;
    } finally {
      setRendering(false);
    }
  };

  // Change this scene's music pick before it's rendered. Optimistic: update the
  // control immediately, persist the FULL entry (so a partial PATCH can't erase
  // sibling fields), revert on failure. Empty value = Auto.
  const handleMusicChange = async (track: string): Promise<void> => {
    const prev = musicChoice;
    if (track === prev) return;
    setMusicChoice(track);
    setMusicSaving(true);
    setRenderError(null);
    try {
      // Raw value, NOT `|| undefined`: selecting Auto sends "" so the server's
      // `"musicTrack" in body` guard fires and CLEARS the pin. `undefined` would
      // be stripped by JSON.stringify, the guard would skip, and the pin would
      // bounce back (the resync effect re-reads the still-pinned entry).
      const { id: _id, createdAt: _createdAt, ...rest } = entry;
      const updated = await updateBacklog(entry.id, {
        ...rest,
        musicTrack: track.trim(),
      });
      onEntryUpdated?.(updated);
    } catch {
      setMusicChoice(prev); // revert — the pick didn't persist
      setRenderError("Couldn't save the music choice — try again.");
    } finally {
      setMusicSaving(false);
    }
  };

  // Confidence pill color: >= 0.7 green, >= 0.4 amber, else red. Mirrors
  // the prior ScanCandidateCard logic exactly.
  const pillColor =
    entry.confidence === undefined
      ? "rgba(255,255,255,.10)"
      : entry.confidence >= 0.7
      ? "rgba(74,222,128,.20)"
      : entry.confidence >= 0.4
      ? "rgba(255,213,0,.20)"
      : "rgba(248,113,113,.20)";
  const pillTextColor =
    entry.confidence === undefined
      ? "var(--ql-fg-muted)"
      : entry.confidence >= 0.7
      ? "var(--ql-success)"
      : entry.confidence >= 0.4
      ? "var(--ql-yellow)"
      : "var(--ql-danger)";

  // Channel chip label (CHANNELS_ENABLED only): resolve the entry's slug to a
  // displayName; fall back to the raw slug when channels prop is empty/missing.
  const channelSlug = entry.channelSlug || DEFAULT_CHANNEL_SLUG;
  const channelLabel = channels?.find((c) => c.slug === channelSlug)?.displayName ?? channelSlug;

  // Compact host · guests · topic line.
  const metaParts = [
    entry.host,
    entry.guests && entry.guests.length > 0 ? entry.guests.join(", ") : undefined,
    entry.topic,
  ].filter((p): p is string => !!p);

  return (
    <div
      style={{
        background: "rgba(255,255,255,.03)",
        border: "1px solid rgba(255,255,255,.08)",
        borderRadius: 14,
        // No `overflow: hidden` here: the music Select's list is positioned
        // inside this row and would be cut at its bottom edge. The thumbnail
        // clips itself (with the row's radius) so the look is the same.
        // Rendered rows dim like before; archived (un-rendered) rows dim a
        // touch further — shelved, but Render/Open still fully functional.
        opacity: isRendered ? 0.7 : entry.archived ? 0.55 : 1,
        transition: "opacity .2s var(--ql-ease)",
      }}
    >
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 0 }}>
        {/* Thumbnail — hidden on mobile to stack content vertically. */}
        {!isMobile && thumbnailUrl && (
          <div
            style={{
              width: 110,
              flexShrink: 0,
              background: "rgba(0,0,0,.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              borderRadius: "14px 0 0 14px",
            }}
          >
            <img
              src={thumbnailUrl}
              alt="video thumbnail"
              style={{ width: "100%", display: "block", objectFit: "cover", aspectRatio: "16/9" }}
            />
          </div>
        )}

        {/* Content — click anywhere here (no interactive children) for the
            scene-details popup: summary, payoff, ending check, transcript. */}
        <div
          onClick={() => setDetailOpen(true)}
          title="Scene details — summary, ending, transcript"
          style={{ flex: 1, padding: "12px 14px", minWidth: 0, cursor: "pointer" }}
        >
          {entry.sourceTitle && (
            <div
              style={{
                fontSize: 11,
                color: "var(--ql-fg-subtle)",
                marginBottom: 4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {entry.sourceTitle}
            </div>
          )}

          {/* Channel chip — CHANNELS_ENABLED only. */}
          {channelsEnabled && (
            <div style={{ marginBottom: 6 }}>
              <span
                style={{
                  display: "inline-block",
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.07)",
                  color: "var(--ql-fg-muted)",
                  border: "1px solid rgba(255,255,255,.10)",
                }}
              >
                {channelLabel}
              </span>
            </div>
          )}

          {/* Hook */}
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ql-fg)", lineHeight: 1.4, marginBottom: 6 }}>
            {entry.hookEn ?? entry.label ?? "(no hook)"}
          </div>

          {/* Meta: origin/confidence pill + time range */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {entry.origin === "locate" ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "var(--ql-font-mono)",
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "rgba(96,165,250,.14)",
                  color: "#60a5fa",
                  border: "1px solid rgba(96,165,250,.35)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Crosshair size={10} strokeWidth={2.5} /> From your anchor
              </span>
            ) : entry.confidence !== undefined ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: "var(--ql-font-mono)",
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: pillColor,
                  color: pillTextColor,
                  border: `1px solid ${pillColor}`,
                }}
              >
                {Math.round(entry.confidence * 100)}% match
              </span>
            ) : null}
            <span style={{ fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)" }}>
              {entry.from ?? "?"} → {entry.to ?? "auto"}
            </span>
          </div>

          {/* Why it works — gold-tinted note box (same as ScanCandidateCard). */}
          {entry.whyItWorks && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderRadius: 10,
                background: "rgba(255,213,0,.04)",
                border: "1px solid rgba(255,213,0,.12)",
                fontSize: 12,
                color: "var(--ql-fg-muted)",
                lineHeight: 1.5,
              }}
            >
              {entry.whyItWorks}
            </div>
          )}

          {/* Compact host · guests · topic. */}
          {metaParts.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--ql-fg-subtle)" }}>
              {metaParts.join(" · ")}
            </div>
          )}

          {renderError && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--ql-danger)" }}>{renderError}</div>
          )}
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            alignItems: isMobile ? "center" : "flex-end",
            gap: 8,
            padding: "12px 14px",
            flexShrink: 0,
            flexWrap: isMobile ? "wrap" : undefined,
          }}
        >
          {/* Per-scene music pick — set it BEFORE rendering. Inherited from the
              New Job form; "" = Auto (the pipeline's pickMusic chooses). Hidden
              once rendered (swap music on a finished clip from its Job page). */}
          {!isRendered && (
            // A div, not a <label>: a label forwards clicks on the icon to the
            // Select's button, which would toggle the list on top of the
            // outside-click close.
            <div
              title="Music for this scene — pick a track or leave on Auto"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                color: "var(--ql-fg-muted)",
                maxWidth: isMobile ? "100%" : 200,
              }}
            >
              <Music size={13} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.8 }} />
              <Select
                aria-label="Background music for this scene"
                value={musicChoice}
                disabled={rendering || musicSaving}
                onChange={(v) => void handleMusicChange(v)}
                size="sm"
                style={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
                options={[
                  { value: "", label: "Auto (picked for you)" },
                  // Keep a pinned track VISIBLE even when the catalog is still
                  // loading, failed to load, or the track was since deleted —
                  // otherwise the control renders blank while the render still
                  // ships this track (the value would match no option).
                  ...(musicChoice && !(musicTracks ?? []).some((t) => t.filename === musicChoice)
                    ? [{
                        value: musicChoice,
                        label: `${prettifyTrackName(musicChoice)}${musicTracks === null ? " (loading…)" : " (unavailable)"}`,
                      }]
                    : []),
                  ...(musicTracks ?? []).map((t) => ({ value: t.filename, label: prettifyTrackName(t.filename) })),
                ]}
              />
            </div>
          )}
          {isRendered ? (
            <>
              {/* Read-only reminder of the track "Render again" will reuse —
                  the editable picker is hidden once rendered (swap music on a
                  finished clip from its Job page instead). */}
              <span
                title="Music baked into this render — change it on the clip's Job page"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 10,
                  color: "var(--ql-fg-muted)",
                  opacity: 0.8,
                  maxWidth: isMobile ? "100%" : 200,
                }}
              >
                <Music size={11} strokeWidth={2} style={{ flexShrink: 0 }} />
                {entry.musicTrack ? prettifyTrackName(entry.musicTrack) : "Auto"}
              </span>
              {/* Open the existing render. */}
              {onNavigateQueue && entry.renderedJobId && (
                <button
                  onClick={() => onNavigateQueue(entry.renderedJobId!)}
                  title="Open the render this scene produced"
                  style={{ ...ctaSmallStyle, cursor: "pointer" }}
                >
                  <ExternalLink size={13} strokeWidth={2} /> Open render
                </button>
              )}
              {/* Re-run the same render path (force past the already-rendered guard). */}
              <button
                onClick={() => void handleRender(true)}
                disabled={rendering}
                title="Render this scene again"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  background: "rgba(255,255,255,.03)",
                  border: "1px solid rgba(255,255,255,.12)",
                  color: "var(--ql-fg-muted)",
                  cursor: rendering ? "wait" : "pointer",
                  opacity: rendering ? 0.6 : 1,
                }}
              >
                {rendering ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        animation: "ql-spin 1s linear infinite",
                        width: 13,
                        height: 13,
                        border: "2px solid currentColor",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                      }}
                    />{" "}
                    Starting…
                  </>
                ) : (
                  <>
                    <RotateCcw size={13} strokeWidth={2} /> Render again
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => void handleRender()}
              disabled={rendering || musicSaving}
              title={musicSaving ? "Saving music choice…" : "Render this scene"}
              style={{ ...ctaSmallStyle, cursor: rendering || musicSaving ? "wait" : "pointer", opacity: rendering || musicSaving ? 0.6 : 1 }}
            >
              {rendering ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      animation: "ql-spin 1s linear infinite",
                      width: 13,
                      height: 13,
                      border: "2px solid currentColor",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                    }}
                  />{" "}
                  Starting…
                </>
              ) : (
                <>
                  <Film size={13} strokeWidth={2} /> Render
                </>
              )}
            </button>
          )}

          {/* Archive (fresh rows) / Restore (archived rows) — archive-not-delete,
              the row just moves between the Fresh and Archived filter chips.
              Rendered rows carry no archive affordance. */}
          {onToggleArchive && !isRendered && (
            entry.archived ? (
              <button
                onClick={onToggleArchive}
                title="Restore this scene to Fresh"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  background: "rgba(255,255,255,.03)",
                  border: "1px solid rgba(255,255,255,.12)",
                  color: "var(--ql-fg-muted)",
                  cursor: "pointer",
                }}
              >
                <ArchiveRestore size={13} strokeWidth={2} /> Restore
              </button>
            ) : (
              <button
                onClick={onToggleArchive}
                title="Archive this scene — it moves to the Archived chip, nothing is deleted"
                style={sceneRowIconBtnStyle}
              >
                <Archive size={14} strokeWidth={1.85} />
              </button>
            )
          )}

          {/* Inline-confirm delete — first click asks, "Yes" deletes, "Cancel" backs out. */}
          {confirming ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>Delete this scene?</span>
              <button
                onClick={onConfirmDelete}
                style={{
                  padding: "5px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(248,113,113,.40)",
                  background: "rgba(248,113,113,.14)",
                  color: "var(--ql-danger)",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "inherit",
                }}
              >
                Yes
              </button>
              <button
                onClick={onCancelDelete}
                style={{
                  padding: "5px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.12)",
                  background: "rgba(255,255,255,.03)",
                  color: "var(--ql-fg-muted)",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={onRequestDelete} title="Remove from library" style={sceneRowIconBtnStyle}>
              <Trash2 size={14} strokeWidth={1.85} />
            </button>
          )}
        </div>
      </div>

      {/* Scene-details popup. Footer reuses THIS row's render flow so the
          operator can read → decide → render without closing the popup. */}
      {detailOpen && (
        <SceneDetailModal
          entry={entry}
          isMobile={isMobile}
          onClose={() => setDetailOpen(false)}
          // Boundary adjustments persist server-side; bubble the updated entry
          // up so the row (and a later Render) sees the adjusted window.
          onEntryUpdated={onEntryUpdated}
          footer={
            isRendered ? (
              onNavigateQueue && entry.renderedJobId ? (
                <button
                  onClick={() => {
                    setDetailOpen(false);
                    onNavigateQueue(entry.renderedJobId!);
                  }}
                  title="Open the render this scene produced"
                  style={{ ...ctaSmallStyle, cursor: "pointer" }}
                >
                  <ExternalLink size={13} strokeWidth={2} /> Open render
                </button>
              ) : undefined
            ) : (
              <button
                // Close the popup once the job actually starts (read → decide →
                // render → back to the list); on failure it stays open so the
                // row's error message context isn't lost behind a closed modal.
                onClick={() =>
                  void handleRender().then((started) => {
                    if (started) setDetailOpen(false);
                  })
                }
                disabled={rendering || musicSaving}
                title={musicSaving ? "Saving music choice…" : "Render this scene"}
                style={{
                  ...ctaSmallStyle,
                  cursor: rendering || musicSaving ? "wait" : "pointer",
                  opacity: rendering || musicSaving ? 0.6 : 1,
                }}
              >
                {rendering ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        animation: "ql-spin 1s linear infinite",
                        width: 13,
                        height: 13,
                        border: "2px solid currentColor",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                      }}
                    />{" "}
                    Starting…
                  </>
                ) : (
                  <>
                    <Film size={13} strokeWidth={2} /> Render
                  </>
                )}
              </button>
            )
          }
        />
      )}
    </div>
  );
}

const sceneRowIconBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.02)",
  color: "var(--ql-fg-muted)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
};
